import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { applyChartThemeDefaults } from '../utils/chartTheme';

const ThemeContext = createContext(undefined);

const THEME_KEY = 'redeancora-theme';
const VALID_THEMES = ['light', 'dark'];

/**
 * Detects OS-level preferred color scheme.
 * Falls back to 'dark' (Bloomberg default).
 */
function getSystemTheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
}

/**
 * Gets the initial theme from localStorage or OS preference.
 */
function getInitialTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored && VALID_THEMES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage not available (SSR, privacy mode, etc.)
  }
  return getSystemTheme();
}

/**
 * Applies the theme to the document root element.
 */
function applyTheme(theme) {
  const root = document.documentElement;
  
  // Add transitioning class for smooth color transitions
  root.setAttribute('data-theme-transitioning', '');
  root.setAttribute('data-theme', theme);
  
  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', theme === 'dark' ? '#0E2038' : '#F8F9FC');
  }
  
  // Remove transitioning class after animation completes
  requestAnimationFrame(() => {
    setTimeout(() => {
      root.removeAttribute('data-theme-transitioning');
    }, 400);
  });
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Alinha os defaults do Chart.js ao tema (eixos, títulos e legendas em
  // quase-branco no dark — cinza médio é ilegível sobre o navy) e repinta os
  // gráficos já montados. Efeito do provider roda depois dos filhos, então
  // cobre também a primeira montagem.
  useEffect(() => {
    applyChartThemeDefaults(theme === 'dark');
  }, [theme]);

  // Persist theme to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Silently fail if localStorage unavailable
    }
  }, [theme]);

  // Listen for OS theme changes (if user hasn't manually set a preference)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const stored = localStorage.getItem(THEME_KEY);
      if (!stored) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setTheme = useCallback((newTheme) => {
    if (VALID_THEMES.includes(newTheme)) {
      setThemeState(newTheme);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const isDark = theme === 'dark';

  const value = React.useMemo(
    () => ({ theme, setTheme, toggleTheme, isDark }),
    [theme, setTheme, toggleTheme, isDark]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context.
 * @returns {{ theme: 'light' | 'dark', setTheme: Function, toggleTheme: Function, isDark: boolean }}
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
